import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icons,
  iconSize,
  Link,
} from '@munifocus/ui';
import type { HazardKind, PropertyHazardView } from '@munifocus/contracts-gov';

/** What a warning needs to say about one hazard. Map entries carry only the kind. */
export type HazardLine = Pick<PropertyHazardView, 'kind'> &
  Partial<Pick<PropertyHazardView, 'note' | 'reviewDue'>>;

function HazardList({ hazards }: { hazards: HazardLine[] }) {
  const { t } = useTranslation();
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
      {hazards.map((h, i) => (
        <li key={`${h.kind}-${i}`}>
          <strong>{t(`gov.safety.hazards.kinds.${h.kind}`)}</strong>
          {h.note ? ` — ${h.note}` : ''}
          {h.reviewDue ? ` (${t('gov.safety.hazards.reviewDue')})` : ''}
        </li>
      ))}
    </Box>
  );
}

/**
 * The prominent warning at the top of a file: every active hazard at the
 * property, before anything else on the page. `role="alert"` so a screen
 * reader announces it on arrival.
 */
export function HazardBanner({ hazards }: { hazards: HazardLine[] }) {
  const { t } = useTranslation();
  if (!hazards.length) return null;
  return (
    <Alert
      severity="error"
      role="alert"
      icon={<Icons.AlertTriangle size={iconSize.lg} aria-hidden />}
      sx={{ mb: 2 }}
    >
      <AlertTitle>{t('gov.safety.hazards.warningCount', { count: hazards.length })}</AlertTitle>
      <HazardList hazards={hazards} />
    </Alert>
  );
}

/** Unique kinds, for a map entry. */
export function kindsToLines(kinds: HazardKind[]): HazardLine[] {
  return [...new Set(kinds)].map((kind) => ({ kind }));
}

/**
 * A directions link that, when the property has hazards, asks the officer to
 * read them first. Without hazards it is a plain link to Google Maps in a new
 * tab, exactly as before.
 */
export function DirectionsLink({
  href,
  address,
  hazards,
}: {
  href: string;
  address: string;
  hazards: HazardLine[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!hazards.length) return;
    e.preventDefault();
    setOpen(true);
  };
  const go = () => {
    setOpen(false);
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        onClick={onClick}
        aria-label={t('gov.visits.directionsTo', { address })}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
      >
        <Icons.Navigation size={iconSize.sm} aria-hidden />
        {t('gov.visits.directions')}
      </Link>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('gov.safety.hazards.beforeNavigation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('gov.safety.hazards.beforeNavigationBody')}</DialogContentText>
          <Alert severity="error" sx={{ mt: 2 }} icon={false}>
            <HazardList hazards={hazards} />
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.actions.cancel')}</Button>
          <Button variant="contained" onClick={go}>
            {t('gov.safety.hazards.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
